import React from 'react'
import { 
  Box, 
  Button, 
  Typography, 
  useTheme 
} from '@mui/material'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import type { ChartConfig, StyledCurveReference, YAxisPosition } from '@/interfaces/waveChart'
import { getCurveId, getCleanReference } from '@/interfaces/waveChart'
import { CurveChip } from '@/components/curves/CurveChip'

export interface ChartAxisManagerProps {
  /** Chart configuration containing curves and axes */
  chart: ChartConfig
  
  /** Whether interaction is disabled */
  disabled?: boolean
  
  /** Callback when a curve is clicked */
  onCurveClick?: (chartId: string, curveId: string) => void
  
  /** Callback when an axis is clicked */
  onAxisClick?: (chartId: string, axisId: YAxisPosition) => void
  
  /** Callback when a curve is moved between axes */
  onCurveMoveAxis?: (chartId: string, curveRef: StyledCurveReference, fromAxis: YAxisPosition, toAxis: YAxisPosition) => void
  
  /** Callback when curves are reordered within the same axis for z-index layering */
  onCurveReorder?: (chartId: string, axisId: YAxisPosition, fromIndex: number, toIndex: number) => void
  
  /** Callback when a curve is removed from the chart */
  onCurveRemove?: (chartId: string, curveId: string) => void
}

/**
 * ChartAxisManager - Simplified drag and drop for managing curves across chart axes
 */
export function ChartAxisManager({
  chart,
  disabled = false,
  onCurveClick,
  onAxisClick,
  onCurveMoveAxis,
  onCurveReorder,
  onCurveRemove
}: ChartAxisManagerProps) {
  const theme = useTheme()
  const [activeCurve, setActiveCurve] = React.useState<StyledCurveReference | null>(null)

  // Configure sensors for better drag behavior
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require 5px movement before drag starts - allows clicks to work
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Group curves by axis
  const curvesByAxis = React.useMemo(() => {
    const grouped: Record<YAxisPosition, StyledCurveReference[]> = {
      left: [],
      right: [],
      left2: [],
      right2: []
    }
    
    chart.curveReferences.forEach(curveRef => {
      const axisId = curveRef.styling?.yAxisId || 'left'
      grouped[axisId].push(curveRef)
    })
    
    return grouped
  }, [chart.curveReferences])

  // Drag handlers
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event
    const curveData = active.data.current

    if (curveData?.type === 'curve-within-chart') {
      setActiveCurve(curveData.curveRef)
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setActiveCurve(null)

    if (!over || !active.data.current) return

    const curveData = active.data.current as { type: string; curveRef: StyledCurveReference; fromAxis: YAxisPosition }
    const overData = over.data.current

    // Moving between axes (dropping on axis drop zone)
    if (curveData?.type === 'curve-within-chart' && overData?.type === 'axis') {
      const fromAxis = curveData.fromAxis
      const toAxis = overData.axisId as YAxisPosition

      if (fromAxis !== toAxis) {
        onCurveMoveAxis?.(chart.id, curveData.curveRef, fromAxis, toAxis)
      }
      return
    }

    // Reordering within same axis (dropping on another curve)
    // With @dnd-kit sortable, when dropping on another sortable item, over.id contains the target item's ID
    if (curveData?.type === 'curve-within-chart' && over.id && typeof over.id === 'string') {
      const fromAxis = curveData.fromAxis
      const axisCurves = curvesByAxis[fromAxis]
      const oldIndex = axisCurves.findIndex(c => getCurveId(c.reference) === getCurveId(curveData.curveRef.reference))
      const newIndex = axisCurves.findIndex(c => getCurveId(c.reference) === over.id)

      // Only reorder if within same axis and indices are valid
      if (oldIndex !== newIndex && oldIndex !== -1 && newIndex !== -1) {
        onCurveReorder?.(chart.id, fromAxis, oldIndex, newIndex)
      }
    }
  }

  // Click handlers
  const handleAxisClick = React.useCallback((axisId: YAxisPosition) => {
    if (!disabled) {
      onAxisClick?.(chart.id, axisId)
    }
  }, [disabled, onAxisClick, chart.id])
  
  const handleCurveClick = React.useCallback((curveRef: StyledCurveReference) => {
    if (!disabled) {
      onCurveClick?.(chart.id, getCurveId(getCleanReference(curveRef)))
    }
  }, [disabled, onCurveClick, chart.id])
  
  const handleCurveRemove = React.useCallback((curveRef: StyledCurveReference) => {
    if (!disabled) {
      onCurveRemove?.(chart.id, getCurveId(getCleanReference(curveRef)))
    }
  }, [disabled, onCurveRemove, chart.id])

  // Get axis display info
  const getAxisDisplayInfo = (axisId: YAxisPosition): { positionalTitle: string; customLabel?: string } => {
    const config = chart.yAxes?.[axisId]
    
    const positionalTitles: Record<YAxisPosition, string> = {
      left: 'LEFT Y-AXIS',
      right: 'RIGHT Y-AXIS', 
      left2: 'LEFT Y-AXIS 2',
      right2: 'RIGHT Y-AXIS 2'
    }
    
    const positionalTitle = positionalTitles[axisId]
    
    let customLabel: string | undefined
    if (config?.label && config?.unit) {
      customLabel = `${config.label} (${config.unit})`
    } else if (config?.label) {
      customLabel = config.label
    } else if (config?.unit) {
      customLabel = `(${config.unit})`
    }
    
    return { positionalTitle, customLabel }
  }

  const getAxisColor = (axisId: YAxisPosition): string => {
    const config = chart.yAxes?.[axisId]
    if (config?.color) return config.color
    
    const defaultColors: Record<YAxisPosition, string> = {
      left: theme.palette.primary.main,
      right: theme.palette.secondary.main,
      left2: theme.palette.success.main,
      right2: theme.palette.warning.main
    }
    
    return defaultColors[axisId]
  }

  const axes: YAxisPosition[] = ['left', 'right', 'left2', 'right2']

  if (disabled) {
    // Simple static version when disabled
    return (
      <Box 
        sx={{
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden'
        }}
      >
        {axes.map((axisId) => {
          const curves = curvesByAxis[axisId]
          const hasConfig = !!chart.yAxes?.[axisId]
          
          return (
            <SimpleAxisSection
              key={axisId}
              axisId={axisId}
              curves={curves}
              hasConfig={hasConfig}
              disabled={disabled}
              getAxisDisplayInfo={getAxisDisplayInfo}
              onAxisClick={() => handleAxisClick(axisId)}
              onCurveClick={handleCurveClick}
              onCurveRemove={onCurveRemove ? handleCurveRemove : undefined}
            />
          )
        })}
      </Box>
    )
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <Box 
        sx={{
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden'
        }}
      >
        {axes.map((axisId) => {
          const curves = curvesByAxis[axisId]
          const hasConfig = !!chart.yAxes?.[axisId]
          
          return (
            <DraggableAxisSection
              key={axisId}
              axisId={axisId}
              curves={curves}
              hasConfig={hasConfig}
              axisColor={getAxisColor(axisId)}
              getAxisDisplayInfo={getAxisDisplayInfo}
              onAxisClick={() => handleAxisClick(axisId)}
              onCurveClick={handleCurveClick}
              onCurveRemove={onCurveRemove ? handleCurveRemove : undefined}
            />
          )
        })}
      </Box>

      <DragOverlay>
        {activeCurve ? (
          <CurveChip
            curveReference={getCleanReference(activeCurve)}
            isDragged={true}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

// Simple axis section for disabled state
interface SimpleAxisSectionProps {
  axisId: YAxisPosition
  curves: StyledCurveReference[]
  hasConfig: boolean
  disabled: boolean
  getAxisDisplayInfo: (axisId: YAxisPosition) => { positionalTitle: string; customLabel?: string }
  onAxisClick: () => void
  onCurveClick: (curveRef: StyledCurveReference) => void
  onCurveRemove?: (curveRef: StyledCurveReference) => void
}

function SimpleAxisSection({
  axisId,
  curves,
  hasConfig,
  disabled,
  getAxisDisplayInfo,
  onAxisClick,
  onCurveClick,
  onCurveRemove
}: SimpleAxisSectionProps) {
  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        '&:last-child': { borderBottom: 0 }
      }}
    >
      <AxisHeader
        axisId={axisId}
        positionalTitle={getAxisDisplayInfo(axisId).positionalTitle}
        customLabel={getAxisDisplayInfo(axisId).customLabel}
        curveCount={curves.length}
        hasConfig={hasConfig}
        disabled={disabled}
        onClick={onAxisClick}
      />

      <Box sx={{ px: 2, pb: curves.length > 0 ? 1 : 0 }}>
        {curves.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {curves.map((curveRef) => {
              const curveId = getCurveId(curveRef.reference)
              
              return (
                <CurveChip
                  key={curveId}
                  curveReference={curveRef.reference}
                  disabled={disabled}
                  onClick={() => onCurveClick(curveRef)}
                  onRemove={onCurveRemove ? () => onCurveRemove(curveRef) : undefined}
                  showRemoveButton={!!onCurveRemove}
                />
              )
            })}
          </Box>
        )}
      </Box>
    </Box>
  )
}

// Draggable axis section with proper drop zones
interface DraggableAxisSectionProps {
  axisId: YAxisPosition
  curves: StyledCurveReference[]
  hasConfig: boolean
  axisColor: string
  getAxisDisplayInfo: (axisId: YAxisPosition) => { positionalTitle: string; customLabel?: string }
  onAxisClick: () => void
  onCurveClick: (curveRef: StyledCurveReference) => void
  onCurveRemove?: (curveRef: StyledCurveReference) => void
}

function DraggableAxisSection({
  axisId,
  curves,
  hasConfig,
  axisColor,
  getAxisDisplayInfo,
  onAxisClick,
  onCurveClick,
  onCurveRemove
}: DraggableAxisSectionProps) {
  const { isOver, setNodeRef } = useDroppable({
    id: axisId,
    data: {
      type: 'axis',
      axisId
    }
  })

  return (
    <Box
      ref={setNodeRef}
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        '&:last-child': { borderBottom: 0 },
        backgroundColor: isOver ? `${axisColor}15` : 'transparent',
        transition: 'background-color 0.2s ease',
        position: 'relative'
      }}
    >
      {/* Simple drop indicator */}
      {isOver && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            border: `2px dashed ${axisColor}`,
            borderRadius: 1,
            pointerEvents: 'none',
            zIndex: 1
          }}
        />
      )}

      <AxisHeader
        axisId={axisId}
        positionalTitle={getAxisDisplayInfo(axisId).positionalTitle}
        customLabel={getAxisDisplayInfo(axisId).customLabel}
        curveCount={curves.length}
        hasConfig={hasConfig}
        disabled={false}
        onClick={onAxisClick}
      />

      <Box sx={{ px: 2, pb: curves.length > 0 ? 1 : 0 }}>
        {curves.length > 0 ? (
          <SortableContext
            items={curves.map(c => getCurveId(c.reference))}
            strategy={verticalListSortingStrategy}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {curves.map((curveRef: StyledCurveReference) => (
                <SortableCurveChip
                  key={getCurveId(curveRef.reference)}
                  curveRef={curveRef}
                  axisId={axisId}
                  onCurveClick={onCurveClick}
                  onCurveRemove={onCurveRemove}
                />
              ))}
            </Box>
          </SortableContext>
        ) : (
          // Empty state with better drop target
          <Box sx={{ minHeight: 24, py: 1 }}>
            {isOver && (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Drop curve here
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}

// Axis header component
interface AxisHeaderProps {
  axisId: YAxisPosition
  positionalTitle: string
  customLabel?: string
  curveCount: number
  hasConfig: boolean
  disabled: boolean
  onClick: () => void
}

function AxisHeader({ axisId: _axisId, positionalTitle, customLabel, curveCount, hasConfig: _hasConfig, disabled, onClick }: AxisHeaderProps) {
  return (
    <Button
      variant="text"
      onClick={onClick}
      disabled={disabled}
      sx={{
        justifyContent: 'flex-start',
        textTransform: 'none',
        width: '100%',
        px: 2,
        py: 1.5,
        '&:hover': {
          backgroundColor: 'action.hover'
        }
      }}
    >
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        width: '100%'
      }}>
        <Box sx={{ flex: 1, textAlign: 'left' }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 'medium',
              color: 'text.secondary',
              display: 'block',
              fontSize: '0.6875rem'
            }}
          >
            {positionalTitle}
          </Typography>
          
          {customLabel && (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'medium',
                color: 'text.primary',
                mt: 0.25,
                lineHeight: 1.2
              }}
            >
              {customLabel}
            </Typography>
          )}
        </Box>

        {curveCount > 0 && (
          <Typography variant="caption" color="text.secondary">
            {curveCount}
          </Typography>
        )}
      </Box>
    </Button>
  )
}

// Sortable curve chip component
interface SortableCurveChipProps {
  curveRef: StyledCurveReference
  axisId: YAxisPosition
  onCurveClick?: (curveRef: StyledCurveReference) => void
  onCurveRemove?: (curveRef: StyledCurveReference) => void
}

function SortableCurveChip({
  curveRef,
  axisId,
  onCurveClick,
  onCurveRemove
}: SortableCurveChipProps) {
  const curveId = getCurveId(getCleanReference(curveRef))
  const cleanRef = getCleanReference(curveRef)
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: curveId,
    data: {
      type: 'curve-within-chart',
      curveRef,
      fromAxis: axisId
    }
  })

  const handleClick = (e: React.MouseEvent) => {
    // Only handle click if we're not in the middle of dragging
    if (!isDragging && onCurveClick) {
      e.stopPropagation()
      console.log('🖱️ Curve clicked:', curveRef.reference.curveName)
      onCurveClick(curveRef)
    }
  }

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  }

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={handleClick}
    >
      <CurveChip
        curveReference={cleanRef}
        onRemove={onCurveRemove ? () => onCurveRemove(curveRef) : undefined}
        showRemoveButton={!!onCurveRemove}
      />
    </Box>
  )
} 